import { Helmet } from 'react-helmet-async';
import { seoConfig } from '@/config/seo';

interface ServiceSchemaProps {
  service: {
    name: string;
    description: string;
    url?: string;
    image?: string;
    category?: string;
    serviceType?: string;
    provider?: string;
    areaServed?: string[];
    offers?: Array<{
      name: string;
      description: string;
      price?: string;
      priceCurrency?: string;
      availability?: string;
    }>;
    aggregateRating?: {
      ratingValue: string;
      reviewCount: string;
      bestRating?: string;
      worstRating?: string;
    };
    reviews?: Array<{
      author: string;
      rating: string;
      reviewBody: string;
      datePublished: string;
    }>;
  };
}

export const ServiceSchema: React.FC<ServiceSchemaProps> = ({ service }) => {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${service.url || seoConfig.site.url}#service`,
    name: service.name,
    description: service.description,
    url: service.url || seoConfig.site.url,
    image: service.image || seoConfig.defaultMeta.ogImage,
    category: service.category || 'Technology Services',
    serviceType: service.serviceType || service.category,
    provider: {
      '@type': 'Organization',
      '@id': `${seoConfig.site.url}#organization`,
      name: service.provider || seoConfig.organization.name,
      url: seoConfig.organization.url,
      logo: seoConfig.organization.logo,
      telephone: seoConfig.organization.telephone,
      email: seoConfig.organization.email,
      address: {
        '@type': 'PostalAddress',
        addressLocality: seoConfig.organization.address.addressLocality,
        addressRegion: seoConfig.organization.address.addressRegion,
        addressCountry: seoConfig.organization.address.addressCountry,
      },
    },
    areaServed: service.areaServed?.map(area => ({
      '@type': 'Place',
      name: area,
    })) || [
      { '@type': 'Country', name: 'United States' },
      { '@type': 'State', name: 'California' },
    ],
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceUrl: service.url || `${seoConfig.site.url}/contact`,
      serviceName: 'Online Consultation',
      availableLanguage: 'English',
    },
    termsOfService: `${seoConfig.site.url}/terms`,
    serviceOutput: {
      '@type': 'Thing',
      name: `${service.name} Implementation`,
      description: `Completed ${service.name.toLowerCase()} solution`,
    },
    hasOfferCatalog: service.offers ? {
      '@type': 'OfferCatalog',
      name: `${service.name} Packages`,
      itemListElement: service.offers.map((offer, index) => ({
        '@type': 'Offer',
        position: index + 1,
        name: offer.name,
        description: offer.description,
        price: offer.price,
        priceCurrency: offer.priceCurrency || 'USD',
        availability: offer.availability || 'https://schema.org/InStock',
        validFrom: new Date().toISOString(),
        seller: {
          '@type': 'Organization',
          '@id': `${seoConfig.site.url}#organization`,
        },
      })),
    } : undefined,
    aggregateRating: service.aggregateRating ? {
      '@type': 'AggregateRating',
      ratingValue: service.aggregateRating.ratingValue,
      reviewCount: service.aggregateRating.reviewCount,
      bestRating: service.aggregateRating.bestRating || '5',
      worstRating: service.aggregateRating.worstRating || '1',
    } : undefined,
    review: service.reviews?.map(review => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.author,
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating,
        bestRating: '5',
      },
      reviewBody: review.reviewBody,
      datePublished: review.datePublished,
    })),
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Enterprise Businesses',
    },
    brand: {
      '@type': 'Brand',
      name: seoConfig.organization.name,
      logo: seoConfig.organization.logo,
    },
    isRelatedTo: [
      {
        '@type': 'Service',
        name: 'Digital Transformation',
      },
      {
        '@type': 'Service',
        name: 'Enterprise Software Development',
      },
    ],
    potentialAction: {
      '@type': 'ConsumeAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${seoConfig.site.url}/contact?service=${encodeURIComponent(service.name)}`,
        actionPlatform: [
          'https://schema.org/DesktopWebPlatform',
          'https://schema.org/MobileWebPlatform',
        ],
      },
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
    </Helmet>
  );
};